import { type TFilterWithViewAllRef } from '@/components';
import {
	ALERT_41,
	MasterInfoEnum,
	PageTypeEnumSelectLodging,
	ROUTERS,
	TextMessage,
} from '@/constant';
import {
	searchParamsKey,
	useCommonList,
	useCreateListSubmit,
	useRowSelection,
	useToggle,
} from '@/hooks';
import { ApiResponseError, ICommonLodging } from '@/services';
import {
	useAppSelector,
	WorkerApplicantActions,
	WorkerApplicantSelectors,
	WorkerFranchiseSelectors,
	WorkerLodgingActions,
	WorkerRecommenderSelectors,
} from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import { filter, NOOP, showAlert } from '@/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { TableRowSelection } from 'antd/es/table/interface';

import { omit } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import {
	getColumns,
	initialValues,
	selectLodgingModalMap,
	TriggerSelectLodgingClick,
	TValues,
} from './select-lodging-modal.config';
import { DataType, IProps } from './select-lodging-modal.type';

export const useLogic = (props: IProps) => {
	const { id } = useParams<{ id: string }>();

	const { providerTable, callBackFetchingData, providerId, object } = props;

	const franchise = useAppSelector(WorkerFranchiseSelectors.selectDetail);
	const recommender = useAppSelector(
		WorkerRecommenderSelectors.selectWorkerRecommenderDetail
	);
	const workerApplicantLodging = useAppSelector(
		WorkerApplicantSelectors.selectApplicantLodging
	);
	const { isWorkerPublicAdminRole } = useSelector(
		MyPageSelectors.selectDataMyPage
	);

	const rowSelection = useRowSelection<TableRowSelection<DataType>, DataType>({
		onChange(selectedRowKeys, selectedRows: any, info) {
			if (selectedRowKeys?.length > 10) {
				showAlert(TextMessage.ALERT_51);
				return;
			}

			rowSelection.setState({ selectedRowKeys, selectedRows });
		},
		type:
			providerTable === PageTypeEnumSelectLodging.APPLICANT
				? 'radio'
				: 'checkbox',
	});

	const [params, setParams] = useState(initialValues);
	const [openModal, onToggle] = useToggle(false);

	const {
		commonList: { fetching, data },
	} = useCommonList<ICommonLodging>(
		MasterInfoEnum.COMMON_LODGING,
		openModal,
		omit(['providerId', 'providerTable'], params)
	);

	const dispatch = useDispatch();

	const { selectorParams } = useMemo(
		() => selectLodgingModalMap[providerTable],
		[providerTable]
	);

	const paramsFetchingData = useSelector(selectorParams);

	const filterRef = useRef<TFilterWithViewAllRef>(null);

	useEffect(() => {
		TriggerSelectLodgingClick.show = () => {
			onToggle();
			filterRef.current?.onReset();
			setParams(initialValues);
			rowSelection.onReset();
		};
	}, []);

	const fetchData = useCallback((params: TValues) => {
		setParams((prev) => ({
			...prev,
			...params,
		}));
	}, []);

	const columns = useMemo(getColumns, []);

	const onSaveClick = useCallback(() => {
		if (providerTable === PageTypeEnumSelectLodging.APPLICANT) {
			dispatch(
				WorkerApplicantActions.createWorkerApplicantLodgingRequest({
					id: Number(id),
					lodgingId: rowSelection.selectedRows?.[0]?.id,
					callback(error: ApiResponseError) {
						if (error) return;
						callBackFetchingData?.(paramsFetchingData);
					},
				})
			);
		} else {
			const isAddressMatch =
				isNotNilOrEmpty(rowSelection.selectedRows?.[0]?.address) &&
				isNotNilOrEmpty(workerApplicantLodging?.[0]?.lodgingAddress) &&
				rowSelection.selectedRows?.[0]?.address ===
					workerApplicantLodging?.[0]?.lodgingAddress;

			if (!isAddressMatch) {
				dispatch(
					WorkerLodgingActions.updateLodgingProviderRequest({
						providerId: Number(id),
						ids: rowSelection.selectedRowKeys as number[],
						providerTable: providerTable,
						providerName: object,
						callback(error: ApiResponseError) {
							if (error) return;
							callBackFetchingData?.(paramsFetchingData);
						},
					})
				);
			}
		}
		onToggle();
	}, [
		providerTable,
		rowSelection.selectedRowKeys,
		workerApplicantLodging,
		callBackFetchingData,
		onToggle,
	]);

	const onCheckIfExists = useCallback(() => {
		if (!rowSelection?.selectedRowKeys?.length) {
			showAlert('숙소을(를) 선택해주세요.');
			return;
		}

		const lodgingDataWithoutCurrentProvider = filter(
			rowSelection?.selectedRows,
			(item) => item?.providerId && Number(item.providerId) !== providerId
		);

		if (
			providerTable === PageTypeEnumSelectLodging.APPLICANT ||
			!lodgingDataWithoutCurrentProvider?.length
		) {
			onSaveClick();
			return;
		}

		showAlert(
			ALERT_41(
				lodgingDataWithoutCurrentProvider
					.map((item) => item.address)
					.join(', '),
				providerTable === PageTypeEnumSelectLodging.RECOMMENDER
					? recommender?.name
					: franchise?.employerName
			),
			onSaveClick,
			NOOP,
			{
				confirmText: '예',
				cancelText: '아니요',
			}
		);
	}, [
		onSaveClick,
		onToggle,
		rowSelection?.selectedRows,
		recommender,
		franchise,
		providerId,
	]);

	const toCreatePathName = useMemo(() => {
		const isApplicant = providerTable === PageTypeEnumSelectLodging.APPLICANT;
		const isFranchise = providerTable === PageTypeEnumSelectLodging.FRANCHISE;
		const queryProps = isApplicant
			? { providerId, providerTable }
			: {
					providerName: isFranchise
						? franchise?.employerName
						: recommender?.name || '',
					providerId: isFranchise ? franchise?.id : recommender?.id,
					providerPhone: isFranchise
						? franchise?.employerPhone
						: recommender?.phone || '',
					providerAddress: isFranchise
						? franchise?.address
						: recommender?.address || '',
					providerTable,
				};
		return `${ROUTERS.LM1_02}?${searchParamsKey}=${encodeURIComponent(JSON.stringify(queryProps))}`;
	}, [franchise, recommender, providerTable, searchParamsKey, providerId]);

	const { onCreateSubmit } = useCreateListSubmit({
		toCreatePathName,
	});

	const onCreateSuccess = useCallback(() => {
		onToggle();
		callBackFetchingData?.(paramsFetchingData);
	}, [paramsFetchingData]);

	const onNavigateLodging = useCallback(() => {
		onCreateSubmit?.({
			callbackSuccess: onCreateSuccess,
		});
	}, [onCreateSubmit, onCreateSuccess]);

	return {
		filterRef,
		initialValues,
		openModal,
		fetching,
		columns,
		dataSource: data,
		rowSelection,
		isWorkerPublicAdminRole,

		fetchData,
		onToggle,
		onCheckIfExists,
		onNavigateLodging,
	};
};
